import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { manageEmailPreferencesActions } from '../../../../../../actions/manage-email-preferences.actions';
import { useAppSelector } from '../../../../../../effects/useAppSelector';
import { AlertOption } from '../../../../../../types/email-preferences/EmailPreferences';
import { UserConfigType } from '../../../../../../types/user-config/UserConfigType';
import { Table } from '../../../../../bidding/common/table';
import { SearchInput, EmptyPlaceholder } from '../../../../../common';
import { cloManagerAlertsColumns } from './clo-managers-im-alerts.columns';
import { CompanyAlertsValue, SecondaryStatsAlertsValue } from '../../../../../../types/user-config/UserConfig';
import { isCollateralManagersOwnCompany } from '../../../../../../utils/amr-pipeline.utils';
import { compareBoolean } from '../../../../../../utils/compare.utils';

interface FilterUpdatesTableProps {
    disabled?: boolean;
}

interface ManagerAlerts {
    referenceName: string;
    legalName: string;
    isUserCompany: boolean;
    cloManagerImAlerts: CompanyAlertsValue | undefined;
    cloManagerApAlerts: CompanyAlertsValue | undefined;
    cloManagerBwicAlerts: SecondaryStatsAlertsValue | undefined;
    cloManagerDiAlerts: SecondaryStatsAlertsValue | undefined;
    cloManagerOutOfNcAlert: CompanyAlertsValue | undefined;
    cloManagerOutOfRiAlert: CompanyAlertsValue | undefined;
}

export const CloManagerImAlertsSection = ({ disabled }: FilterUpdatesTableProps) => {
    const dispatch = useDispatch();
    const userCompany = useAppSelector(s => s.issuanceMonitor.amrPipelineCommon.userCompany);
    const managers = useAppSelector(s => s.manageEmailPreferences.mangers);
    const cloManagerImAlerts = useAppSelector(
        s => s.manageEmailPreferences.editCloManagersPreferences?.cloManagerIssuanceMonitorAlert.value,
    );
    const cloManagerApAlerts = useAppSelector(
        s => s.manageEmailPreferences.editCloManagersPreferences?.cloManagerArrangerPipelineAlert.value,
    );
    const cloManagerBwicAlerts = useAppSelector(
        s => s.manageEmailPreferences.editCloManagersPreferences?.cloManagerBwicAlert.value,
    );
    const cloManagerDealerInventoryAlert = useAppSelector(
        s => s.manageEmailPreferences.editCloManagersPreferences?.cloManagerDealerInventoryAlert.value,
    );
    const cloManagerOutOfNcAlert = useAppSelector(
        s => s.manageEmailPreferences.editCloManagersPreferences?.cloManagerOutOfNcAlert.value,
    );
    const cloManagerOutOfRiAlert = useAppSelector(
        s => s.manageEmailPreferences.editCloManagersPreferences?.cloManagerOutOfRiAlert.value,
    );


    const alertsList = managers?.map(({referenceName, legalName}) => ({
        referenceName,
        legalName,
        isUserCompany: referenceName === userCompany?.referenceName,
        cloManagerImAlerts: cloManagerImAlerts?.find(alert => alert.companyReferenceName === referenceName),
        cloManagerApAlerts: cloManagerApAlerts?.find(alert => alert.companyReferenceName === referenceName),
        cloManagerBwicAlerts: cloManagerBwicAlerts?.find(alert => alert.companyReferenceName === referenceName),
        cloManagerDiAlerts: cloManagerDealerInventoryAlert?.find(alert => alert.companyReferenceName === referenceName),
        cloManagerOutOfNcAlert: cloManagerOutOfNcAlert?.find(alert => alert.companyReferenceName === referenceName),
        cloManagerOutOfRiAlert: cloManagerOutOfRiAlert?.find(alert => alert.companyReferenceName === referenceName),
    }));

    const [searchTerm, setSearchTerm] = useState<string>('');
    const managersList: ManagerAlerts[] | undefined = alertsList
        ?.filter(({ legalName }) =>
            legalName
                ?.toLocaleLowerCase()
                .includes(searchTerm.toLocaleLowerCase())
        )
        .sort((a, b) => compareBoolean(a.isUserCompany, b.isUserCompany));

    const setAlertsOptions = (
        type: UserConfigType,
        mapCallback: (a: CompanyAlertsValue) => CompanyAlertsValue
    ) => {
        switch(type) {
            case UserConfigType.cloManagerIssuanceMonitorAlert:
                dispatch(
                    manageEmailPreferencesActions.setCloManagersIssuanceMonitorAlert({
                        type,
                        value: cloManagerImAlerts!.map(mapCallback),
                    }),
                );
                break;
            case UserConfigType.cloManagerArrangerPipelineAlert:
                dispatch(
                    manageEmailPreferencesActions.setCloManagersArrangerPipelineAlert({
                        type,
                        value: cloManagerApAlerts!.map(mapCallback),
                    }),
                );
                break;
            case UserConfigType.cloManagerBwicAlert:
                dispatch(
                    manageEmailPreferencesActions.setCloManagersBwicAlert({
                        type,
                        value: cloManagerBwicAlerts!.map(mapCallback) as SecondaryStatsAlertsValue[],
                    }),
                );
                break;
            case UserConfigType.cloManagerDealerInventoryAlert:
                dispatch(
                    manageEmailPreferencesActions.setCloManagersDealerInventoryAlert({
                        type,
                        value: cloManagerDealerInventoryAlert!.map(mapCallback) as SecondaryStatsAlertsValue[],
                    }),
                );
                break;
            case UserConfigType.cloManagerOutOfNcAlert:
                dispatch(
                    manageEmailPreferencesActions.setCloManagersOutOfNcAlert({
                        type,
                        value: cloManagerOutOfNcAlert!.map(mapCallback)
                    }),
                );
                break;
            case UserConfigType.cloManagerOutOfRiAlert:
                dispatch(
                    manageEmailPreferencesActions.setCloManagersOutOfRiAlert({
                        type,
                        value: cloManagerOutOfRiAlert!.map(mapCallback)
                    }),
                );
                break;
            default:
                break;
        }
    };

    const onColumnClick = (type: UserConfigType, referenceName: string, alertOption: AlertOption) => {
        const mapList = (a: CompanyAlertsValue) => a.companyReferenceName === referenceName ? { ...a, alertOption } : a;
        setAlertsOptions(type, mapList);
    };

    const onHeaderClick = (type: UserConfigType, option: AlertOption) => {
        const mapList = (a: CompanyAlertsValue) => ({ ...a, alertOption: option });
        setAlertsOptions(type, mapList);
    };

    return (
        <>
            <SearchInput
                onClear={() => setSearchTerm('')}
                value={searchTerm}
                onChange={(searchTerm: string) => setSearchTerm(searchTerm)}
                placeholder="Search by managers"
            />
            {managersList?.length ? (
                <Table
                    columns={cloManagerAlertsColumns}
                    createHeaderCustomArgs={() => ({
                        cloManagerImAlerts,
                        cloManagerApAlerts,
                        cloManagerBwicAlerts,
                        cloManagerDealerInventoryAlert,
                        cloManagerOutOfNcAlert,
                        cloManagerOutOfRiAlert,
                        onHeaderClick,
                        disabled,
                    })}
                    createSecurityCustomArgs={(manager: ManagerAlerts) => ({
                        onColumnClick,
                        disabled,
                        isCollateralCompany: isCollateralManagersOwnCompany(manager, userCompany),
                    })}
                    className="data-list-striped"
                    dataItems={managersList}
                    sticky={true}
                />
            ) : (
                <EmptyPlaceholder className="empty-placeholder-without-margin" />
            )}
        </>
    );
};
